'use client';

import {useState} from 'react';
import Image from 'next/image';
import {RowsPhotoAlbum, type RenderImageContext, type RenderImageProps} from 'react-photo-album';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import {FiMaximize2} from 'react-icons/fi';
import 'react-photo-album/rows.css';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import {track} from '@/lib/analytics';

export type GalleryPhoto = {
  key: string;
  /** Base Sanity CDN URL; next/image adds width/quality. */
  src: string;
  /** Large URL for the lightbox. */
  lightboxSrc: string;
  width: number;
  height: number;
};

const renderNextImage = ({alt = '', title, sizes}: RenderImageProps, {photo, width, height}: RenderImageContext) => (
  <div className='relative w-full cursor-pointer overflow-hidden rounded-xl' style={{aspectRatio: `${width} / ${height}`}}>
    <Image fill src={photo.src} alt={alt} title={title} sizes={sizes} className='object-cover transition-transform duration-500 hover:scale-105' />
  </div>
);

const Gallery = ({photos, context = ''}: {photos: GalleryPhoto[]; context?: string}) => {
  const [index, setIndex] = useState(-1);

  const open = (i: number) => {
    setIndex(i);
    track('gallery_open', {context, image_index: i});
  };
  const close = () => {
    setIndex(-1);
  };

  return (
    <div className='w-full pt-8'>
      <RowsPhotoAlbum
        photos={photos.map((photo, i) => ({
          ...photo,
          alt: `${context ? `${context} – ` : ''}Bild ${i + 1} von ${photos.length}, vergrößern`,
        }))}
        spacing={16}
        defaultContainerWidth={860}
        sizes={{size: '860px', sizes: [{viewport: '(max-width: 900px)', size: 'calc(100vw - 40px)'}]}}
        onClick={({index: i}) => open(i)}
        render={{
          image: renderNextImage,
          extras: () => (
            <div className='pointer-events-none absolute right-3 bottom-3 flex size-9 items-center justify-center rounded-full bg-white/90 text-zinc-900 shadow'>
              <FiMaximize2 />
            </div>
          ),
        }}
      />
      <Lightbox
        open={index >= 0}
        index={index}
        close={close}
        plugins={[Thumbnails]}
        labels={{
          Previous: 'Vorheriges Bild',
          Next: 'Nächstes Bild',
          Close: 'Schließen',
          Lightbox: 'Bildergalerie',
          Carousel: 'Bilder',
          Slide: 'Bild',
          'Photo gallery': 'Bildergalerie',
          Thumbnails: 'Vorschaubilder',
        }}
        on={{view: ({index: i}) => track('gallery_image_view', {context, image_index: i})}}
        slides={photos.map((photo, i) => ({
          src: photo.lightboxSrc,
          width: photo.width,
          height: photo.height,
          alt: `${context ? `${context} – ` : ''}Bild ${i + 1} von ${photos.length}`,
        }))}
      />
    </div>
  );
};

export default Gallery;
